"use client";

import React, { useState } from "react";
import { useRouter } from "next/navigation";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { QuoteRequestForm } from "../shared/QuoteRequestForm";
import { ImageWithFallback } from "../figma/ImageWithFallback";
import { ArrowLeft, Phone, CheckCircle, Star, Award } from "lucide-react";
import { PhoneLink } from "@/utils/internal/linkHelpers";
import { OTHER_SERVICES_PAGE } from "@/utils/constants/pages/other-services";
import { CONTACT_INFO } from "@/utils/constants/app";


interface OtherServicesPageProps {
  onBackToHome?: () => void;
  activeTab?: string;
  serviceImages?: Record<string, string | null>;
}

export function OtherServicesPage({ onBackToHome, activeTab: initialActiveTab, serviceImages }: Readonly<OtherServicesPageProps>) {
  const [activeTab, setActiveTab] = useState(initialActiveTab || "pest-management");
  const router = useRouter();
  console.log("Url of serviceImages:", serviceImages);

  return (
     
    <div className="min-h-screen bg-white">
      {/* Header with Back Button */}
      <div className="bg-green-600 text-white py-6">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center mb-4">
            
      <Button
              variant="ghost"
        onClick={() => (onBackToHome ? onBackToHome() : router.push("/"))}
              className="text-white hover:bg-green-700 mr-4"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              {OTHER_SERVICES_PAGE.HEADER.BACK_BUTTON}
            </Button>
            
          </div>
          <div className="text-center">
            <h1 className="text-4xl lg:text-5xl font-bold mb-4">
              {OTHER_SERVICES_PAGE.HEADER.TITLE}
            </h1>
            <p className="text-xl opacity-90 max-w-3xl mx-auto">
              {OTHER_SERVICES_PAGE.HEADER.SUBTITLE}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-[1320px] mx-auto px-6">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            {/* Tabs Navigation */}
            <TabsList className="!grid w-full grid-cols-2 lg:grid-cols-4 gap-3 mb-12 !h-auto p-4 bg-gray-100 rounded-2xl">
              {OTHER_SERVICES_PAGE.SERVICES.map((service) => {
                const IconComponent = service.icon;
                return (
                  <TabsTrigger 
                    key={service.id} 
                    value={service.id}
                    className="w-full min-h-[64px] flex flex-col items-center p-4 rounded-2xl data-[state=active]:bg-green-600 data-[state=active]:text-white"
                  >
                    <IconComponent className="w-6 h-6 mb-2" />
                    <span className="text-sm font-medium text-center leading-tight">
                      {service.title}
                    </span>
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {/* Tabs Content */}
            {OTHER_SERVICES_PAGE.SERVICES.map((service) => {
              const IconComponent = service.icon;
              return (
                <TabsContent key={service.id} value={service.id} className="mt-0">
                  <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
                    {/* Service Details */}
                    <div>
                      <div className="flex items-center mb-6">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mr-4">
                          <IconComponent className="w-8 h-8 text-green-600" />
                        </div>
                        <div>
                          <h2 className="text-3xl lg:text-4xl text-gray-900 mb-2">{service.title}</h2>
                          <p className="text-lg text-green-600 font-medium">{service.subtitle}</p>
                        </div>
                      </div>

                      <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                        {service.description}
                      </p>

                      <div className="mb-8">
                        <h3 className="text-xl font-semibold text-gray-900 mb-4">
                          {OTHER_SERVICES_PAGE.SECTIONS.FEATURES_TITLE}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {service.features.map((feature) => (
                            <div key={feature} className="flex items-center">
                              <CheckCircle className="w-5 h-5 text-green-600 mr-3 flex-shrink-0" />
                              <span className="text-gray-700">{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="mb-8">
                        <h3 className="text-xl font-semibold text-gray-900 mb-4">
                          {OTHER_SERVICES_PAGE.SECTIONS.BENEFITS_TITLE}
                        </h3>
                        <div className="space-y-2">
                          {service.benefits.map((benefit) => (
                            <div key={benefit} className="flex items-center">
                              <Star className="w-5 h-5 text-yellow-400 mr-3 flex-shrink-0" />
                              <span className="text-gray-700">{benefit}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="bg-green-50 p-6 rounded-xl mb-8">
                        <h4 className="font-semibold text-green-800 mb-3">
                          {OTHER_SERVICES_PAGE.SECTIONS.DETAILS_TITLE}
                        </h4>
                        <p className="text-green-700 leading-relaxed">{service.detailedInfo}</p>
                      </div>

                      <div className="flex flex-col sm:flex-row gap-4 max-w-3xl">
                        <QuoteRequestForm 
                          showPopupButton={true}
                          popupButtonText={OTHER_SERVICES_PAGE.BUTTONS.PRIMARY}
                          popupButtonClassName="cta-button-enhanced bg-green-600 hover:bg-green-700 text-white px-8 py-4 text-lg font-semibold rounded-lg flex-1 sm:flex-auto sm:min-w-[220px]"
                          title={`${OTHER_SERVICES_PAGE.FORM.TITLE_PREFIX} ${service.title}`}
                          subtitle={OTHER_SERVICES_PAGE.FORM.SUBTITLE}
                          defaultService={service.defaultService}
                        />
                        <PhoneLink
                          phoneNumber={CONTACT_INFO.MAIN_PHONE}
                          className="inline-flex items-center justify-center px-6 py-3 text-base font-medium border-2 border-green-600 text-green-600 hover:bg-green-600 hover:text-white rounded-lg transition-colors duration-300 flex-shrink-0 sm:min-w-[180px]"
                        >
                          <Phone className="w-4 h-4 mr-2" />
                          Chiama Ora
                        </PhoneLink>
                      </div>
                    </div>

                    {/* Service Image & Guarantees */}
                    <div className="space-y-6">
                      <div className="relative">
                        <ImageWithFallback
                          src={
                            serviceImages?.[service.id]
                              ?? (service.id === 'pest-management' ? encodeURI('/foto 9.jpeg') : service.image)
                          }
                          alt={service.title}
                          className="rounded-2xl shadow-2xl w-full h-80 lg:h-96 object-cover"
                        />
                      </div>

                      <Card className="border-0 shadow-lg">
                        <CardHeader className="bg-gray-50">
                          <CardTitle className="flex items-center">
                            <Award className="w-6 h-6 text-green-600 mr-2" />
                            {OTHER_SERVICES_PAGE.GUARANTEES.TITLE}
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="p-6 space-y-4">
                          {OTHER_SERVICES_PAGE.GUARANTEES.LIST.map((guarantee) => (
                            <div key={guarantee} className="flex items-center">
                              <CheckCircle className="w-5 h-5 text-green-600 mr-3" />
                              <span>{guarantee}</span>
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>

          {/* Call to Action Section */}
          
        </div>
      </section>
    </div>
  );
}